import { ReactNode } from "react";
import { CommentThread } from "./CommentThread";
import { CommentViewContext } from "./interface";
import { ResolveThreadCommentsButton } from "./ResolveThreadCommentsButton";
import { shouldShowCommentThread } from "./shouldShowCommentThread";
import { sortComments } from "./sortComments";
import { useComments } from "./useComments";

interface CommentThreads {
  activeContext: CommentViewContext;
  tocId?: string;
}

/**
 * Display all top-level comment threads, filtered and sorted appropriately.
 *
 * @param props - Component props.
 * @param props.activeContext - Active comment view context.
 * @param props.tocId - Identifier of the ToC item whose comments are shown.
 */
export const CommentThreads = ({ activeContext, tocId }: CommentThreads): ReactNode => {
  const { comments, commentFilter } = useComments(activeContext, tocId);
  const parentComments = comments.filter((c) => c.parentId === undefined);
  const visibleComments = parentComments
    .sort(sortComments)
    .filter((comment) => shouldShowCommentThread(comment.id, comments, commentFilter));

  return (
    <>
      {parentComments.length === 0 ? (
        <span className="text-muted text-center d-block">
          <em>No comments</em>
        </span>
      ) : visibleComments.length === 0 ? (
        <span className="text-muted text-center d-block">
          <em>No {commentFilter} comments</em>
        </span>
      ) : (
        visibleComments.map((comment) => (
          <div
            key={comment.id}
            className="comment-initial-thread"
          >
            {activeContext !== "Toc" && (
              <ResolveThreadCommentsButton
                activeContext={activeContext}
                commentId={comment.id}
              />
            )}
            <CommentThread
              id={comment.id}
              activeContext={activeContext}
              tocId={tocId}
            />
          </div>
        ))
      )}
    </>
  );
};

CommentThreads.displayName = "CommentThreads";
